import { Plus, Upload } from "lucide-react";
import Button from "../../../components/common/button";
import { BankTable } from "./bankTable";
import React, { useEffect, useRef, useState } from "react";
import Modal from "../../../components/common/modal";
import Ic_file from "../../../assets/images/Ic_file.svg";
import Ic_close from "../../../assets/images/Ic_close.svg";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import FileInfo from "../../../components/FileInfo";
import { toast } from "react-hot-toast";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../config/firebaseConfig";
import { useAuthData } from "../../../context/AuthContext";

const formSchema = z.object({
  document: z.union([
    z
      .instanceof(File)
      .refine((file: any) => file === null || file.size <= 15 * 1024 * 1024, {
        message: "Filstørrelsen må være mindre enn 15 MB.",
      }),
    z.string(),
  ]),
});

export const AllBankLeads = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popup = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { adminData } = useAuthData();

  const form = useForm<any>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    console.log(data);
    navigate("/add-agent-leads");
  };

  const uploadDocument = form.watch("document");

  const documentInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleDocumentUpload = async (files: FileList, fieldName: string) => {
    if (!files.length) return;

    const file = files[0];

    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image size must be less than 15MB.", {
        position: "top-right",
      });
      return;
    }

    const fileType = "documents";
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${fileType}/${fileName}`);

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);

      form.setValue(fieldName, downloadUrl);
      form.clearErrors(fieldName);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload document.", {
        position: "top-right",
      });
    }
  };

  const handleDocumentClick = () => {
    documentInputRef.current?.click();
  };
  const handleDocumentDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDocumentDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      await handleDocumentUpload(event.dataTransfer.files, "document");
    }
  };

  const handleDocumentChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files) {
      await handleDocumentUpload(event.target.files, "document");
    }
  };

  const [Role, setRole] = useState<any>(null);
  const [id, setId] = useState<any>(null);

  useEffect(() => {
    const getData = async () => {
      if (adminData) {
        setId(adminData?.id);

        if (adminData?.role) {
          setRole(adminData?.role);
        }
      }
    };

    getData();
  }, [adminData]);

  const ssoCalledRef = useRef(false);

  useEffect(() => {
    if (ssoCalledRef.current) return;

    const handleSSO = async () => {
      if (
        adminData?.role === "Agent" &&
        adminData?.is_boligkonfigurator === true
      ) {
        if (!adminData?.email) return;

        // Mark as called BEFORE making the request
        ssoCalledRef.current = true;

        try {
          const response = await fetch("https://www.iplot.no/api/sso/handoff", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_HAND_OFF}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: adminData.email,
              source: "boligpartner",
            }),
          });

          if (!response.ok) {
            throw new Error(`SSO handoff failed: ${response.status}`);
          }

          const data = await response.json();

          if (data?.url) {
            window.location.href = data.url;
          }
        } catch (error) {
          console.error("SSO error:", error);
        }
      }
    };

    handleSSO();
  }, [adminData]);

  return (
    <>
      <div className="px-6 pt-6 pb-16 flex flex-col gap-4 md:gap-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-darkBlack font-medium text-xl md:text-2xl desktop:text-[30px]">
              Søknader sendt til bank
            </h1>
            <p className="text-gray text-sm md:text-base">
              Liste over alle dine søknader om banklån
            </p>
          </div>
          <div className="flex gap-3">
            {Role && Role !== "Bankansvarlig" && (
              <div
                className="text-primary text-sm md:text-base lg:text-lg font-semibold flex items-center gap-4 cursor-pointer"
                onClick={() => {
                  const url = `${process.env.REACT_APP_REDIRECT_URL_BOLIGPARTNER}?id=${id}`;
                  window.location.href = url;
                }}
              >
                <Button
                  text="Gå til Boligkonfigurator"
                  className="border-2 border-primary bg-white text-primary text-sm rounded-[8px] h-[40px] font-medium relative px-3 md:px-4 py-[10px] flex items-center gap-2"
                />
              </div>
            )}
            <Button
              text="Nytt tips"
              className="border-2 border-primary bg-primary text-white text-sm rounded-[8px] h-[40px] font-medium relative px-3 md:px-4 py-[10px] flex items-center gap-2"
              icon={<Plus className="text-white w-5 h-5" />}
              path="/add-agent-leads"
              // onClick={() => setIsPopupOpen(true)}
            />
          </div>
        </div>
        <BankTable />
      </div>

      {isPopupOpen && (
        <Modal
          isOpen={true}
          onClose={() => setIsPopupOpen(false)}
          outSideClick={true}
        >
          <div
            className="bg-white p-4 md:p-8 rounded-lg max-w-full md:max-w-2xl w-full relative"
            ref={popup}
          >
            <button
              className="absolute top-2 md:top-3 right-1 md:right-3"
              onClick={() => navigate("/add-agent-leads")}
            >
              <img src={Ic_close} alt="close" />
            </button>

            <div>
              <h4 className="font-semibold text-black3 text-base md:text-lg mb-1">
                Økonomisk plan/kontrakt
              </h4>
              <p className="text-gray4 text-xs md:text-sm mb-5">
                Vi analyserer kontrakten din og fyller automatisk ut alle
                viktige detaljer. Du kan når som helst se gjennom og redigere de
                utvunnede dataene.
              </p>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="relative"
                >
                  <div>
                    <FormField
                      control={form.control}
                      name="document"
                      render={() => (
                        <FormItem className="w-full">
                          <FormControl>
                            <div className="relative w-full p-2 rounded-lg bg-skyBlue">
                              <div className="bg-white p-1">
                                <div
                                  className="flex gap-4 items-center p-2 justify-center border-gray3 border rounded-lg shadow-shadow5 h-[120px]"
                                  onDragOver={handleDocumentDragOver}
                                  onClick={handleDocumentClick}
                                  onDrop={handleDocumentDrop}
                                >
                                  <div className="bg-primary h-12 w-12 justify-center items-center flex rounded-full">
                                    <Upload className="text-white" />
                                  </div>
                                  <div className="flex flex-col gap-1.5">
                                    <div className="text-black3 font-semibold text-xs md:text-sm">
                                      Last opp dokumenter
                                    </div>
                                    <p className="text-gray4 text-xs md:text-sm">
                                      Slipp fil for å laste opp her
                                    </p>
                                  </div>

                                  <input
                                    type="file"
                                    ref={documentInputRef}
                                    className="hidden"
                                    accept=".pdf"
                                    onChange={(e) => handleDocumentChange(e)}
                                    name="document"
                                  />
                                </div>
                              </div>
                              <p className="text-gray4 text-xs md:text-sm text-center w-full mt-3">
                                Filformater: kun{" "}
                                <span className="text-black3">
                                  PDF, max 15 MB
                                </span>
                              </p>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {typeof uploadDocument === "string" && (
                      <div className="border border-gray3 rounded-lg p-2 md:p-3 flex items-center justify-between relative w-full mt-5">
                        <div className="flex items-start gap-2 md:gap-4 truncate">
                          <div className="bg-lightGreen w-10 h-10 rounded-full flex justify-center items-center">
                            <img src={Ic_file} alt="file" />
                          </div>
                          <FileInfo file={uploadDocument} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end w-full gap-5 items-center z-50 mt-5">
                    <div onClick={() => form.reset()}>
                      <Button
                        text="Avbryt"
                        className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                      />
                    </div>
                    <div>
                      <Button
                        text="OK"
                        className="border border-primary bg-primary text-white text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                        type="submit"
                      />
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
